import { Component, OnInit, HostListener, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomerModel } from './models/customer.model';
import { regex } from '../../common/regex.constants';
import { VehicleModel } from './models/vehicle.model';
import { TranslationService } from '../../common/services/languages/translation.service';
import { CustomerDispatcher } from '../../store/customer/customer-dispatcher';
import { StepDispatcher } from '../../store/steps-store/step-dispatcher';
import { VehicleDispatcher } from '../../store/vehicle-store/vehicle-dispatcher';
import { MatSnackBar } from '@angular/material';
import { ApiResponseDispatcher } from '../../store/api-response/api-response-dispatcher';
import { OrchestrationService } from '../services/orchestration-service/orchestration.service';
import { select } from '@angular-redux/store';
import { Observable, Subscription } from 'rxjs';
import { ApiResponseState } from '../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../store/api-response/api-response-actions';
import { CommonDispatcher } from '../../store/common/common-dispatcher';
import { AdminApiHandlerService } from '../services/api-handler/api-handler.service';
import { environment } from '../../../environments/environment';
import { SpinnerService } from '../../common/components/spinner/overlay-spinner.service';
import { AuthenticationService } from 'src/app/authentication/authentication.service';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, NgIf, MatIcon, MatSuffix, MatError, MatHint, TranslatePipe]
})
export class SearchBarComponent implements OnInit, OnDestroy {
  /**
   *@var to store  input field email or phone or vin
   **/
  public search_attribute: UntypedFormGroup;
  /**
   *@var to store the status of the search bar ( Customer or Profile)
   **/
  public profile: string;
  /**
   *@var to store the placeholder of the form
   **/
  public placeholder: string;
  /**
   *@var to store the model (CUstomer or Vehicle)
   **/
  public model: any;
  /**
  *@arg  observe api variable in redux
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
   * @property used to store the  subscription
   */
  public subscription: Subscription;
  /**
   * @property used to show content
   */
  @Output() showResults: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
 *@property to store the flag if the vehicle has customer association without subscription
 **/
  public vehicleAssociationApiCallCount: number;
  /**
 *@property to store the active vehicle owner GUID
 **/
  public vehicleAssocActiveGuid: any;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public fb: UntypedFormBuilder,
    public translate: TranslationService,
    private apiDispatcher: ApiResponseDispatcher,
    public dispatcher: CustomerDispatcher,
    public stepDispatcher: StepDispatcher,
    public vehicleDispatcher: VehicleDispatcher,
    private readonly snackBar: MatSnackBar,
    public orch: OrchestrationService,
    public customerDispatcher: CustomerDispatcher,
    public common: CommonDispatcher,
    public apiHandler: AdminApiHandlerService,
    public spinner: SpinnerService,
    public authService: AuthenticationService,
  ) {
    this.subscription = this.apiObservable.subscribe((api) => {
      let temp = api.apiAction === ApiResponseActions.SET_CUSTOMER && api.customer['type'] === 'success' && !!api.customer['customer'] ?
        this.findCustomerSuccess(api.customer['customer']) : api.customer['type'] === 'error' ? this.findCustomerError(api.customer) : null;
      temp = api.apiAction === ApiResponseActions.SET_VINLIST && api.vinlist['type'] === 'success' ?
        this.findVehicleSuccess(api.vinlist) : api.vinlist['type'] === 'error' ? this.findVehicleError(api.vinlist) : null;
    });
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit(): void {
    this.profile = '';
    this.setDefaultPlaceholders();
    this.search_attribute = this.fb.group(this.bindings);
    this.showContent('clear');
    const vinFromURl = this.authService.getVinFromURL();
    if (vinFromURl) {
      this.vehicleAssociationApiCallCount = 0;
      this.profile = 'vehicle';
      this.search_attribute.controls.searchAttribute.setValue(vinFromURl);
      this.authService.clearVinFromURL();
      this.search();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  /**
   * bindings returns the defaults
   **/
  public get bindings(): object {
    return {
      searchAttribute: ['',
        [Validators.required,
        Validators.maxLength(50),
        Validators.pattern(regex.emailPhoneVin)]]
    };
  }
  /**
   * @method onClick should dimiss the snack bar on click event
   * @returns void
   */
  @HostListener('click', ['$event'])
  public onClick(): void {
    if (!!this.snackBar) { this.snackBar.dismiss(); }
  }
  /**
   * setDefaultPlaceholders Defaults placeholders
   **/
  public setDefaultPlaceholders(): void {
    this.placeholder = 'search-bar.placeholder';
  }
  /**
   * onKeyUp updates the form value
   **/
  public onKeyUp(): void {
    this.vehicleAssociationApiCallCount = 0;
    let inputValue = this.search_attribute.controls.searchAttribute.value;
    if (!inputValue.startsWith('+') && (regex.phoneNumberPattern.test(inputValue) ||
      regex.phonePatternWithChar.test(inputValue))) { inputValue = '+1' + inputValue; }
    this.search_attribute.controls.searchAttribute.setValue(inputValue.replace(/ /g, '').trim());
    this.search_attribute.controls.searchAttribute.updateValueAndValidity();
  }

  /**
   * validateSearchAttribute Validates the search attribute and sets the profile to vehicle or customer
   **/
  public validateSearchAttribute(): void {
    this.profile = regex.vinPattern.test(this.search_attribute.controls.searchAttribute.value) ? 'vehicle' : 'customer';
    this.common.setProfile(this.profile);
  }
  /**
   * findVehicle Calls the find vehicle APi
   **/
  public get isVinSearch() {
    return this.profile === 'vehicle';
  }
  /**
   * createModel creates the model- vehicle or customer
   **/
  public createModel(): void {
    this.model = this.isVinSearch ? new VehicleModel(this.search_attribute.controls.searchAttribute.value) :
      new CustomerModel(this.search_attribute.controls.searchAttribute.value);
  }
  /**
   * search creates the model- vehicle or customer
   * @param $event input object
   **/
  public search($event?): void {

    this.stepDispatcher.storeGuidValue(null);
    this.showContent('clear');
    this.common.resetAll();
    this.validateSearchAttribute();
    this.createModel();
    this.searchProfile();
  }
  /**
   * searchProfile Initiates the method calls to search the respective profile
   **/
  public searchProfile(): void {
    this.model.error = false;
    this.defaults();
    if (this.isVinSearch) {
      this.findVehicle();
    } else {
      this.findCustomer();
    }
  }
  /**
   * findCustomer actions necessary to find customer
   **/
  public findCustomer(): void {
    this.model.setMode = 'read';
    this.vehicleAssocActiveGuid = '';
    this.orch.searchCustomer('customer', this.model.getRouteParams);
  }
  /**
   * findVehicle action to find vehicle (api call)
   **/
  public findVehicle(): void {
    this.orch.getVehicleByVin({ vin: this.model.vin.toUpperCase() });
  }
  /**
   * @function findVehicleSuccess success of find vehicle api
   * @param {object} response response from the api
   **/
  public findVehicleSuccess(response: object): void {
    this.stepDispatcher.isVehicleAssociated({
      isVehicleAssociated: false,
      vehicleAssociatedGuid: ''
    });
    this.vehicleAssociationApiCallCount++;
    if (response['payload'].length === 0) {
      if (!!this.model.vin) {
        this.orch.findVehicle(this.model.vin.toUpperCase(), 'vinList');
      }
    }
    setTimeout(() => {
      if (!!this.snackBar) { this.snackBar.dismiss(); }
    }, 9000);
    this.vehicleDispatcher.setRemoteDetails({
      remoteUserGuid: response['remoteUserGuid'],
      remoteSubscriptionStatus: response['remoteSubscriptionStatus']
    });
    if (this.vehicleAssociationApiCallCount === 1 && this.isVinSearch && response['payload'].length > 0 && !response['payload'][0]['subscriberGuid']) {
      const spinnerRef = this.spinner.open();
      this.vehicleAssocActiveGuid = '';
      this.stepDispatcher.isVehicleAssociated({
        isVehicleAssociated: false,
        vehicleAssociatedGuid: ''
      });
      this.apiHandler.getRequest(environment.admin.apis.subscription.getVehicleAssociation, {
        type: 'get-vehicle-association',
        params: {
          brand: response['payload'][0]['brand'],
          vin: response['payload'][0]['vin']
        }
      }).subscribe({
        next: (response) => {
          spinnerRef.close();
          response['payload']['vehicleAssociations'].forEach(vehAssocObject => {
            this.getActiveOwnerGuid(vehAssocObject);
          });
          if (!!response['payload']['vehicleAssociations'] && !!this.vehicleAssocActiveGuid) {
            this.orch.searchCustomer('customer', {
              guid: this.vehicleAssocActiveGuid
            });
          }
        },
        error: (error) => {
          spinnerRef.close();
        }
      })
    }
    this.showContent('');
  }


  /**
  * getActiveOwnerGuid gets the GUID of an active vehicle owner
  **/
  public getActiveOwnerGuid(vehAssocObject) {
    vehAssocObject.vehicles.forEach(vehicle => {
      if (vehicle['status'] === 'ACTIVE') {
        this.vehicleAssocActiveGuid = vehAssocObject['guid']
        this.stepDispatcher.isVehicleAssociated({
          isVehicleAssociated: true,
          vehicleAssociatedGuid: this.vehicleAssocActiveGuid
        });
        this.stepDispatcher.storeGuidValue(this.vehicleAssocActiveGuid);
      }
    });
  }

  /**
  * findCustomerSuccess Action after a successful API response
  * @param response  Response of the API
  **/
  public findCustomerSuccess(customer: any): void {
    this.customerDispatcher.setCustomerData(customer);
    this.customerDispatcher.customerFound(true);
    if (!this.vehicleAssocActiveGuid) {
      this.orch.getVehiclesByGuid(customer);
    }
    this.apiDispatcher.setCustomerResponse({});
    this.showContent('');
  }
  /**
  * errorCallback Action for error after API response
  * @param error  Response of the API
  **/
  public findCustomerError(error: object): void {
    this.apiDispatcher.setCustomerResponse({});
    if (error['customerFound'] === false) {
      this.customerDispatcher.customerNotFoundError(true);
      this.showContent('');
    } else {
      this.orch.showError(error);
    }
  }
  /**
   * setd the default redux
   **/
  public defaults(): void {
    this.resetVehicleStore();
    this.dispatcher.setCustomerData({});
    this.dispatcher.customerFound(false);
    this.dispatcher.customerNotFoundError(false);
    this.dispatcher.setCustomerDetails('', '', '', '');
  }
  /**
   * resetVehicleStore reset vehicle store
   **/
  public resetVehicleStore(): void {
    this.stepDispatcher.storeVin(false);
    this.vehicleDispatcher.resetVehicleStore();
    this.dispatcher.setPrefLangApiCall(false);
  }
  /**
   * findVehicleError Error of find vehicle api
   **/
  public findVehicleError(error: object) {
    this.apiDispatcher.setVinlistResponse({});
    this.showContent('clear');
    if (error['errorCode'] === 'ONE-VL-10002') {
      this.model.error = true;
    } else {
      this.orch.showError(error);
    }
  }
  /**
   * shows the results
   **/
  public showContent(mode: string): void {
    mode === 'clear' ?
      this.showResults.emit(false) :
      this.showResults.emit(true);
  }
  /**
   * clear clear action
   **/
  public clear(): void {
    this.search_attribute.reset();
    this.showContent('clear');
  }
  /**
   * isEmailError to check is email error in input
   */
  public isEmailError(): boolean {
    const searchAttribute: string = this.search_attribute.controls.searchAttribute.value;
    return !!searchAttribute && searchAttribute.indexOf('@') > -1
      && this.profile !== 'vehicle' ? !regex.emailPattern.test(searchAttribute) : false;
  }
}
